import type { CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import { PRODUCT_DIFFICULTY_LEVELS } from 'bundles/search-common/constants';
import type { ProductCardSimilarSpecializationFragmentFragment as ProductCardSimilarSpecializationFragment } from 'bundles/unified-description-page-common/queries/__generated__/ProductCardSimilarSpecializationFragment';
import type { ProductCardSpecializationFragmentFragment } from 'bundles/unified-description-page-common/queries/__generated__/ProductCardSpecializationFragment';
import type { XdpRec_ProductCardFragment as XdpRecProductCardFragment } from 'bundles/unified-description-page-common/queries/__generated__/XdpProductRecommendations';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import getBadgeStringsFromS12nPageData from 'bundles/unified-description-page-common/utils/getBadgeStringsFromS12nPageData';

import _t from 'i18n!nls/xdp';

const DIFFICULTY_LEVEL_MAP = {
  ADVANCED: PRODUCT_DIFFICULTY_LEVELS.Advanced,
  INTERMEDIATE: PRODUCT_DIFFICULTY_LEVELS.Intermediate,
  BEGINNER: PRODUCT_DIFFICULTY_LEVELS.Beginner,
} as const;

type DifficultyLevelKey = keyof typeof DIFFICULTY_LEVEL_MAP;

const mapDifficultyLevel = (difficultyLevel?: DifficultyLevelKey | null) => {
  if (!difficultyLevel) return undefined;
  return DIFFICULTY_LEVEL_MAP[difficultyLevel];
};

const formatProductDurationWithTranslation = (months?: number | null, hoursPerWeek?: number | null) => {
  return months && hoursPerWeek
    ? _t(
        '{totalMonths} {totalMonths, plural, =1 {month} other {months}} at {totalHours} {totalHours, plural, =1 {hour} other {hours}} a week',
        {
          totalMonths: months,
          totalHours: hoursPerWeek,
        }
      )
    : undefined;
};

type GetCollectionEntityProps = {
  productId: string;
  name: string;
  slug: string;
  imageUrl: string;
  badges: string[];
  productType: CollectionEntity['productType'];
  urlPath: string;
  reviewCount: number;
  rating: number;
  isPartOfCourseraPlus: boolean;
  isPathwayContent: boolean;
  productDifficultyLevel: CollectionEntity['productDifficultyLevel'];
  partners: { id: string; name: string; squareLogo: string }[];
  skills?: string[];
  coBrandingEnabled?: boolean;
  productDuration?: string;
  entityIndex: number;
};

const getCollectionEntity = ({
  productId,
  name,
  slug,
  imageUrl,
  badges,
  productType,
  urlPath,
  reviewCount,
  rating,
  isPartOfCourseraPlus,
  isPathwayContent,
  productDifficultyLevel,
  partners,
  skills,
  coBrandingEnabled,
  productDuration,
  entityIndex,
}: GetCollectionEntityProps): CollectionEntity => {
  const entity: CollectionEntity = {
    id: productId,
    name,
    slug,
    imageUrl,
    statusTags: badges ?? [],
    productType,
    link: urlPath,
    numProductRatings: reviewCount,
    avgProductRating: rating,
    isPartOfCourseraPlus,
    isPathwayContent,
    productDifficultyLevel,
    partners:
      partners.map(({ id: partnerId, name: partnerName, squareLogo }) => ({
        id: partnerId,
        name: partnerName,
        logo: squareLogo ?? '',
      })) ?? [],
    partnerLogos: partners.map((partner) => partner.squareLogo).filter(Boolean) ?? [],
    skills,
    coBrandingEnabled,
    productDuration,
    trackingData: {
      id: productId,
      entityId: `${productType}~${productId}`,
      entityIndex,
      name,
      slug,
    },
  };

  return entity;
};

export const convertProductCardSpecializationToCollectionEntity = (
  specialization: ProductCardSpecializationFragmentFragment,
  entityIndex: number
): CollectionEntity | null => {
  if (!('canonical' in specialization.productTypeAttributes)) return null;
  const { marketingProductType, productTypeAttributes, badges } = specialization;
  const { isPartOfCourseraPlus, isPathwayContent, rating, reviewCount } = productTypeAttributes;
  const { id, slug, name, cardImageUrl, difficultyLevel, partners, urlPath } = productTypeAttributes.canonical;

  let productType;
  if (marketingProductType === 'SPECIALIZATION') productType = 'SPECIALIZATION' as const;
  else if (marketingProductType === 'PROFESSIONAL_CERTIFICATE') productType = 'PROFESSIONAL CERTIFICATE' as const;

  return getCollectionEntity({
    productId: id,
    name,
    slug,
    imageUrl: cardImageUrl,
    badges: badges ?? [],
    productType,
    urlPath,
    reviewCount,
    rating,
    isPartOfCourseraPlus,
    isPathwayContent,
    productDifficultyLevel: mapDifficultyLevel(difficultyLevel),
    partners: partners.map((partner) => ({
      id: partner.id,
      name: partner.name,
      squareLogo: partner.squareLogo ?? '',
    })),
    entityIndex,
  });
};

export const convertProductCardS12nOrCourseToCollectionEntity = (
  product: XdpRecProductCardFragment,
  entityIndex: number
): CollectionEntity | null => {
  const { marketingProductType, productTypeAttributes, badges } = product;

  if (productTypeAttributes.__typename === 'ProductCard_Specialization') {
    const { isPartOfCourseraPlus, isPathwayContent, rating, reviewCount } = productTypeAttributes;
    const { cardImageUrl, difficultyLevel, id, name, partners, slug, urlPath } =
      productTypeAttributes.canonicalSpecialization;

    let productType;
    if (marketingProductType === 'SPECIALIZATION') productType = 'SPECIALIZATION' as const;
    else if (marketingProductType === 'PROFESSIONAL_CERTIFICATE') productType = 'PROFESSIONAL CERTIFICATE' as const;

    return getCollectionEntity({
      productId: id,
      name,
      slug,
      imageUrl: cardImageUrl,
      badges: badges ?? [],
      productType,
      urlPath,
      reviewCount,
      rating,
      isPartOfCourseraPlus,
      isPathwayContent,
      productDifficultyLevel: mapDifficultyLevel(difficultyLevel),
      partners: partners.map((partner) => ({
        id: partner.id,
        name: partner.name,
        squareLogo: partner.squareLogo ?? '',
      })),
      entityIndex,
    });
  }

  if (productTypeAttributes.__typename === 'ProductCard_Course') {
    const { difficultyLevel, id, name, partners, slug, promoPhotoUrl } = productTypeAttributes.canonicalCourse;
    const { isPartOfCourseraPlus, isPathwayContent, rating, reviewCount } = productTypeAttributes;
    const productType = 'COURSE' as const;

    return getCollectionEntity({
      productId: id,
      name,
      slug,
      imageUrl: promoPhotoUrl ?? '',
      badges: badges ?? [],
      productType,
      urlPath: `/learn/${slug}`,
      reviewCount,
      rating,
      isPartOfCourseraPlus,
      isPathwayContent,
      productDifficultyLevel: difficultyLevel ? mapDifficultyLevel(difficultyLevel) : undefined,
      partners: partners.map((partner) => ({
        id: partner.id,
        name: partner.name,
        squareLogo: partner.squareLogo ?? '',
      })),
      entityIndex,
    });
  }

  return null;
};

export const convertXdpSimilarProductToCollectionEntity = (
  product: ProductCardSimilarSpecializationFragment,
  entityIndex: number
): CollectionEntity | null => {
  if (product.__typename !== 'ProductCard_ProductCard') {
    return null;
  }

  const { id, marketingProductType, productTypeAttributes, badges } = product;

  if (productTypeAttributes.__typename === 'ProductCard_Specialization') {
    const { isPathwayContent } = productTypeAttributes;
    const {
      cardImageUrl,
      difficultyLevel,
      name,
      partners,
      slug,
      rating,
      urlPath,
      skillTags,
      coBrandingEnabled,
      monthsToCompleteOverride,
      hoursPerWeekOverride,
    } = productTypeAttributes.canonical;

    let productType;
    if (marketingProductType === 'SPECIALIZATION') productType = 'SPECIALIZATION' as const;
    else if (marketingProductType === 'PROFESSIONAL_CERTIFICATE') productType = 'PROFESSIONAL CERTIFICATE' as const;

    return getCollectionEntity({
      productId: id,
      name,
      slug,
      imageUrl: cardImageUrl,
      badges: badges ?? [],
      productType,
      urlPath,
      isPartOfCourseraPlus: false, // required field, not used in this context
      reviewCount: rating?.ratingCount ?? 0,
      rating: rating?.averageFiveStarRating ?? 0,
      isPathwayContent,
      productDifficultyLevel: mapDifficultyLevel(difficultyLevel),
      partners: partners.map((partner) => ({
        id: partner.id,
        name: partner.name,
        squareLogo: partner.squareLogo ?? '',
      })),
      skills: skillTags?.map((skill) => skill.skillName) ?? [],
      coBrandingEnabled,
      productDuration: formatProductDurationWithTranslation(monthsToCompleteOverride, hoursPerWeekOverride),
      entityIndex,
    });
  }

  return null;
};

export const convertSpecializationPageToCollectionEntity = (
  specialization: ConsumerSpecializationPageFieldsFragment,
  entityIndex: number
): CollectionEntity | null => {
  const {
    id,
    slug,
    name,
    cardImageUrl,
    difficultyLevel,
    partners,
    productVariant,
    ratings,
    skillTags,
    learningHoursPerWeek,
    totalLearningMonths,
    coBrandingEnabled,
    degrees,
  } = specialization;

  if (productVariant === 'STANDARD_SPECIALIZATION' || productVariant === 'PROFESSIONAL_CERTIFICATE') {
    let productType;
    if (productVariant === 'STANDARD_SPECIALIZATION') productType = 'SPECIALIZATION' as const;
    else if (productVariant === 'PROFESSIONAL_CERTIFICATE') productType = 'PROFESSIONAL CERTIFICATE' as const;
    else productType = 'SPECIALIZATION' as const;

    return getCollectionEntity({
      productId: id,
      name,
      slug,
      imageUrl: cardImageUrl ?? '',
      badges: getBadgeStringsFromS12nPageData(specialization),
      productType,
      urlPath: productType === 'SPECIALIZATION' ? `/specializations/${slug}` : `/professional-certificates/${slug}`,
      reviewCount: ratings?.ratingCount ?? 0,
      rating: ratings?.averageFiveStarRating ?? 0,
      isPartOfCourseraPlus: false, // required field, not used in this context
      isPathwayContent: !!degrees && degrees.length > 0,
      productDifficultyLevel: mapDifficultyLevel(difficultyLevel),
      partners: (partners ?? []).map((partner) => ({
        id: partner.id,
        name: partner.name,
        squareLogo: partner.squareLogo ?? '',
      })),
      skills: skillTags?.map((skill) => skill.name) ?? [],
      coBrandingEnabled: coBrandingEnabled ?? undefined,
      productDuration: formatProductDurationWithTranslation(totalLearningMonths, learningHoursPerWeek),
      entityIndex,
    });
  }

  return null;
};
